import React, { useEffect, useRef, useState } from "react";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import styles from "./DefineGeoFence.module.scss";
import { withTranslation } from "react-i18next";
import { useGeoFencePolygonDraw } from "./useGeoFencePolygonDraw";

const DEFAULT_ZOOM = 14;

function Map({
  centerLatLng,
  polygonCoords,
  onPolygonSet,
  t,
  children,
  defaultZoomProp,
  isGeocoordinateReady = true,
}) {
  const ref = useRef();
  const [map, setMap] = useState(null);
  const defaultZoomValue = defaultZoomProp ? defaultZoomProp : DEFAULT_ZOOM;
  const hasStartedDrawRef = useRef(false);

  const {
    polygon,
    isDrawing,
    draftVertexCount,
    startDrawing,
    finishDrawing,
    cancelDrawing,
    reset,
  } = useGeoFencePolygonDraw({ map, polygonCoords, onPolygonSet });

  useEffect(() => {
    if (map !== null || !ref.current) return;

    const _map = new window.google.maps.Map(ref.current, {
      zoom: defaultZoomValue,
      streetViewControl: false,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
      },
      disableDoubleClickZoom: true,
    });
    setMap(_map);
  }, [map, defaultZoomValue]);

  useEffect(() => {
    if (centerLatLng && map !== null) {
      map.setCenter(centerLatLng);
    }
  }, [centerLatLng, map]);

  // Auto-start drawing once saved geofence load has finished and none exists.
  useEffect(() => {
    if (
      !map ||
      !isGeocoordinateReady ||
      polygon ||
      polygonCoords ||
      isDrawing ||
      hasStartedDrawRef.current
    ) {
      return;
    }

    hasStartedDrawRef.current = true;
    startDrawing();
  }, [
    map,
    isGeocoordinateReady,
    polygon,
    polygonCoords,
    isDrawing,
    startDrawing,
  ]);

  const moveToCenter = () => {
    map.setCenter(centerLatLng);
    map.setZoom(defaultZoomValue);
  };

  const handleReset = () => {
    hasStartedDrawRef.current = false;
    reset();
  };

  return (
    <div className="d-flex flex-column">
      <div className={styles["map-shell"]}>
        <div ref={ref} id="map" className={styles["map"]} />
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { map });
          }
        })}
      </div>

      <div className={styles["map-actions"]}>
        <button
          type="button"
          className={styles["map-action-button"]}
          disabled={!map || !centerLatLng}
          onClick={moveToCenter}
        >
          {t("accountOwner.moveToOfficeLocation")}
        </button>

        {!polygon && !isDrawing && (
          <button
            type="button"
            className={styles["map-action-button"]}
            disabled={!map}
            onClick={() => {
              hasStartedDrawRef.current = true;
              startDrawing();
            }}
          >
            {t("accountOwner.drawGeoFencingArea")}
          </button>
        )}

        {isDrawing && (
          <>
            <button
              type="button"
              className={styles["map-action-button"]}
              disabled={draftVertexCount < 3}
              onClick={finishDrawing}
            >
              {t("accountOwner.finishDrawingGeoFence")}
            </button>
            <button
              type="button"
              className={styles["map-action-button"]}
              onClick={cancelDrawing}
            >
              {t("accountOwner.cancelDrawingGeoFence")}
            </button>
          </>
        )}

        <button
          type="button"
          className={styles["map-action-button"]}
          disabled={polygon === null}
          onClick={handleReset}
        >
          {t("accountOwner.clearGeoFencingArea")}
        </button>
      </div>

      {isDrawing && (
        <p className={styles["draw-hint"]}>
          {t("accountOwner.drawGeoFenceHint", {
            count: draftVertexCount,
          })}
        </p>
      )}
    </div>
  );
}

function MapWrapper(props) {
  return (
    <Wrapper
      apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
      libraries={["places"]}
      render={renderMapStatus}
    >
      <Map {...props} />
    </Wrapper>
  );
}

function renderMapStatus(status) {
  if (status === Status.LOADING) return "Loading Map...";
  if (status === Status.FAILURE) return "Error loading Map.";
  return null;
}

export default withTranslation()(MapWrapper);
